import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, API } from "../lib/api";
import { KpiCard } from "../components/KpiCard";
import { RankBadge } from "../components/RankBadge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { FileDown, Presentation as PresentationIcon } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { WorldClassScoreInfo } from "../components/WorldClassScoreInfo";

const PPTX_ROLES = ["ADMIN", "DIRECTION_SECURITE"];

export default function SiteDetail() {
  const { user } = useAuth();
  const { siteId } = useParams();
  const [searchParams] = useSearchParams();
  const campagneIdParam = searchParams.get("campagne_id") || "";
  const navigate = useNavigate();
  const [site, setSite] = useState(null);
  const [data, setData] = useState(null);
  const [comparison, setComparison] = useState([]);
  const [campagnes, setCampagnes] = useState([]);

  useEffect(() => {
    api.get("/sites").then((res) => setSite(res.data.find((s) => s.site_id === siteId)));
    const params = campagneIdParam ? { campagne_id: campagneIdParam } : {};
    api.get(`/dashboard/site/${siteId}`, { params }).then((res) => setData(res.data));
    api.get(`/comparison/site/${siteId}`).then((res) => setComparison(res.data));
    api.get("/campagnes").then((res) => setCampagnes(res.data));
  }, [siteId, campagneIdParam]);

  if (!site || !data) return <div className="p-6 text-sm text-muted-foreground">Chargement...</div>;

  const ameliorations = comparison.filter((c) => c.evolution === "AMELIORATION").length;
  const regressions = comparison.filter((c) => c.evolution === "REGRESSION").length;
  const campagneNom = (id) => campagnes.find((c) => c.campagne_id === id)?.nom || id;

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-5" data-testid="site-detail-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-heading text-3xl font-black uppercase tracking-tight">{site.nom_site}</h1>
            {data.ranking && <RankBadge rank={data.ranking.rank} worldClassPct={data.ranking.world_class_pct} testId="site-rank-badge" />}
            {data.ranking && <WorldClassScoreInfo testId="site-world-class-info" />}
          </div>
          <p className="text-sm text-muted-foreground">
            {site.categorie}
            {data.ranking_campagne_id && <> · Classement calculé sur : <span data-testid="site-ranking-campagne-label" className="font-medium">{campagneNom(data.ranking_campagne_id)}</span></>}
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate("/sites")}>← Retour aux sites</Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KpiCard title="Progression audit" value={`${data.progression_audit}%`} color="emerald" testId="site-kpi-progression" />
        <KpiCard title="Minimum Req." value={data.score_minimum_moyen} sub="/4" color="sky" testId="site-kpi-min" />
        <KpiCard title="World Class" value={data.score_world_class_moyen} sub="/4" color="sky" testId="site-kpi-wc" />
        <KpiCard title="Non-conformités" value={data.non_conformites} color="rose" testId="site-kpi-nc" />
        <KpiCard title="Actions ouvertes" value={data.actions_ouvertes} color="amber" testId="site-kpi-actions" />
        <KpiCard title="Actions échues" value={data.actions_echues} color="rose" testId="site-kpi-echues" />
        <KpiCard title="Actions critiques" value={data.actions_critiques} color="rose" testId="site-kpi-critiques" />
        <KpiCard title="À revoir" value={data.exigences_a_revoir} color="amber" testId="site-kpi-a-revoir" />
      </div>

      <Card>
        <CardHeader><CardTitle>Résultats par chapitre</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {data.resultats_par_chapitre?.length ? data.resultats_par_chapitre.map((c) => (
            <div key={c.chapitre} className="flex items-center justify-between text-sm border-b py-1.5 last:border-0">
              <span>{c.chapitre}</span>
              <span className="font-mono font-semibold">{c.score_moyen}/4</span>
            </div>
          )) : <p className="text-sm text-muted-foreground">Aucune donnée évaluée.</p>}
        </CardContent>
      </Card>

      <Card data-testid="site-comparison-card">
        <CardHeader><CardTitle>Comparaison 2024 → 2026</CardTitle></CardHeader>
        <CardContent>
          <div className="flex gap-4 text-sm mb-3">
            <span className="text-emerald-600 font-semibold">{ameliorations} amélioration(s)</span>
            <span className="text-rose-600 font-semibold">{regressions} régression(s)</span>
          </div>
          <div className="max-h-64 overflow-y-auto space-y-1">
            {comparison.map((c) => (
              <div key={c.requirement_id} className="flex items-center justify-between text-xs border-b py-1 last:border-0">
                <span className="font-mono">{c.reference}</span>
                <span>{c.score_2024 ?? "-"} → {c.score_2026 ?? "-"}</span>
                <span className={
                  c.evolution === "AMELIORATION" ? "text-emerald-600" :
                  c.evolution === "REGRESSION" ? "text-rose-600" : "text-muted-foreground"
                }>{c.evolution || "-"}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {data.audits?.[0] && (
        <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={() => navigate(`/audits/${data.audits[0].audit_id}`)}>
          Ouvrir l'audit terrain
        </Button>
      )}

      {data.audits?.length > 0 && (
        <Card data-testid="site-reports-card">
          <CardHeader><CardTitle>Rapports PDF</CardTitle></CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {data.audits.map((a) => (
              <div key={a.audit_id} className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  className="gap-2"
                  data-testid={`download-site-report-${a.audit_id}`}
                  onClick={() => window.open(`${API}/reports/site/${a.audit_id}`, "_blank")}
                >
                  <FileDown className="h-4 w-4" /> Télécharger le rapport PDF — {campagneNom(a.campagne_id)}
                </Button>
                {PPTX_ROLES.includes(user?.role) && (
                  <Button
                    variant="outline"
                    className="gap-2"
                    data-testid={`download-site-pptx-${a.audit_id}`}
                    onClick={() => window.open(`${API}/reports/site/${a.audit_id}/pptx`, "_blank")}
                  >
                    <PresentationIcon className="h-4 w-4" /> Synthèse PPTX — {campagneNom(a.campagne_id)}
                  </Button>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
